import { useState } from 'react';
import type { Quote } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE, QUOTE_STATUS_LABELS } from '@catalyst-psa/shared';

import { DetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedBoolean, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { useQuotes } from './api.js';
import { QUOTE_STATUS_TONES } from './status.js';

/**
 * All quotes across opportunities. Quotes are created FROM an opportunity (Quotes tab)
 * — there is deliberately no "New quote" button here.
 */
export function QuotesPage(): JSX.Element {
  const rowLink = useRowLink();
  const [page, setPage] = useState(1);
  const [latestOnly, setLatestOnly] = useState(true);

  const filterDefaults = useFilterDefaults('quotes', (saved) => {
    const savedLatest = savedBoolean(saved, 'latestOnly');
    if (savedLatest !== null) setLatestOnly(savedLatest);
  });

  const query = useQuotes({ page, pageSize: DEFAULT_PAGE_SIZE, latestOnly });
  const quotes = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  return (
    <div>
      <PageHeader
        title="Quotes"
        description="Versioned quotes attached to opportunities. Create quotes from the opportunity's Quotes tab."
      />

      <div className="mb-4 flex items-center gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={latestOnly}
            onChange={(e) => {
              setLatestOnly(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Latest versions only
        </label>
        <FilterDefaultsButtons
          handle={filterDefaults}
          onSave={() => filterDefaults.save({ latestOnly })}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Customer</th>
              <th className="px-4 py-2 font-medium">Opportunity</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium">Version</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={!query.isLoading && quotes.length === 0}
              emptyTitle="No quotes yet"
              emptyDescription="Open an opportunity and use its Quotes tab to create the first quote."
              colSpan={6}
            >
              {quotes.map((quote: Quote) => (
                <tr
                  key={quote.id}
                  {...rowLink(`/sales/quotes/${quote.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-2 font-medium text-slate-800">{quote.customerName}</td>
                  <td className="px-4 py-2 text-slate-600">{quote.opportunityName}</td>
                  <td className="px-4 py-2 text-slate-600">{quote.opportunityTypeName ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">
                    v{quote.version}
                    {quote.isLatest ? (
                      <span className="ml-2 inline-flex">
                        <Badge tone="blue">Latest</Badge>
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    <Badge tone={QUOTE_STATUS_TONES[quote.status]}>
                      {QUOTE_STATUS_LABELS[quote.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-slate-500">{formatDate(quote.updatedAt)}</td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
        {pageInfo && quotes.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>
      <DetailOverlay listPath="/sales/quotes" />
    </div>
  );
}
